import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Check,
    HelpCircle,
    Mountain,
    Route as RouteIcon,
    Layers,
    Shield,
    Droplets,
    Ruler,
    MapPinned,
} from "lucide-react";
import Seo from "@/components/layout/Seo";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import Reveal from "@/components/layout/Reveal";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import PrestationInternalLinks from "@/components/marketing/PrestationInternalLinks";
import ContactForm from "@/components/marketing/ContactForm";
import { SERVICES } from "@/lib/site";
import { buildBreadcrumb, buildService, buildFaqPage, SITE_ORIGIN } from "@/lib/schema";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const SLUG = "amenagements-exterieurs-enrochement";

const PRESTATIONS = [
    {
        icon: <RouteIcon className="w-5 h-5" />,
        title: "Cours, allées et accès extérieurs",
        text: "Nous préparons et réalisons des aménagements extérieurs destinés à faciliter les accès et l’usage quotidien des espaces : cours, allées, accès véhicules, accès piétons, plateformes, zones de circulation ou surfaces extérieures techniques.",
        bullets: [
            "Cours et allées",
            "Accès piétons ou véhicules",
            "Plateformes extérieures",
            "Zones de circulation",
            "Préparation avant revêtement",
        ],
    },
    {
        icon: <Ruler className="w-5 h-5" />,
        title: "Plateformes, niveaux et préparation de surfaces",
        text: "La gestion des niveaux est essentielle pour obtenir un extérieur fonctionnel. RCPS Terrassement intervient pour préparer les plateformes, régler les pentes, adapter les surfaces et organiser les travaux avant revêtement, enrobé, gravier, cour ou aménagement complémentaire.",
        bullets: [
            "Mise à niveau",
            "Préparation de plateformes",
            "Gestion des pentes",
            "Préparation avant enrobé ou gravier",
            "Adaptation du terrain à l’usage prévu",
        ],
    },
    {
        icon: <Mountain className="w-5 h-5" />,
        title: "Enrochement décoratif ou technique",
        text: "L’enrochement permet de structurer un terrain, créer une limite naturelle, accompagner un talus, protéger une zone ou renforcer l’aspect minéral d’un aménagement extérieur. Il peut avoir une fonction esthétique, pratique ou technique selon le projet. L’enrochage est étudié selon la configuration du terrain.",
        bullets: [
            "Enrochement de talus",
            "Blocs d’enrochement",
            "Structuration d’espaces extérieurs",
            "Limite naturelle ou minérale",
            "Enrochage selon configuration du terrain",
        ],
    },
    {
        icon: <Shield className="w-5 h-5" />,
        title: "Talus, soutènements et stabilisation de terrain",
        text: "Les terrains en pente ou soumis à des différences de niveaux peuvent nécessiter une solution de soutènement, de stabilisation ou de protection. L’enrochement peut contribuer à maintenir un talus, structurer une zone ou accompagner un aménagement extérieur, selon les contraintes du terrain et les prestations prévues.",
        bullets: [
            "Stabilisation de talus",
            "Soutènement de terrain",
            "Protection de zones sensibles",
            "Gestion des différences de niveaux",
            "Adaptation selon la nature du sol",
        ],
    },
];

const PROJECT_TYPES = [
    "Création de cour extérieure",
    "Aménagement d’accès véhicule",
    "Allées et cheminements",
    "Plateformes extérieures",
    "Préparation avant enrobé",
    "Préparation avant gravier",
    "Talus et terrains en pente",
    "Enrochement décoratif",
    "Enrochement technique",
    "Soutènement de terrain",
    "Stabilisation de talus",
    "Protection de terrain",
    "Aménagements autour de villas ou immeubles",
    "Travaux extérieurs pour régies, architectes, promoteurs, entreprises générales et collectivités",
];

const METHODE = [
    {
        title: "Analyse du terrain",
        text: "Nous étudions la localisation, les accès, la pente, les niveaux, l’usage prévu de l’espace, la nature visible du terrain et les contraintes principales du chantier.",
        bullets: [
            "Accès au chantier",
            "Niveaux et pentes",
            "Usage futur",
            "Contraintes techniques",
        ],
    },
    {
        title: "Préparation des surfaces",
        text: "Selon le projet, nous préparons les fonds, adaptons les niveaux, organisons les matériaux et préparons la zone pour accueillir une cour, un accès, une plateforme ou un aménagement extérieur.",
        bullets: [
            "Préparation du terrain",
            "Mise à niveau",
            "Gestion des pentes",
            "Adaptation des surfaces",
        ],
    },
    {
        title: "Réalisation de l’aménagement",
        text: "Les travaux sont réalisés selon les prestations prévues : accès, cour, plateforme, talus, soutènement, enrochement ou préparation avant revêtement.",
        bullets: [
            "Cours et accès",
            "Plateformes",
            "Talus et soutènements",
            "Enrochement",
        ],
    },
    {
        title: "Finitions et remise en ordre",
        text: "La zone d’intervention est remise en ordre selon les prestations prévues. Les finitions dépendent du type d’aménagement, des matériaux utilisés et de l’usage futur de l’espace.",
        bullets: [
            "Remise en ordre",
            "Finitions extérieures",
            "Contrôle de la zone",
            "Suivi de fin de chantier",
        ],
    },
];

const VIGILANCE = [
    {
        icon: <Layers className="w-5 h-5" />,
        title: "Gestion des niveaux",
        text: "Les niveaux et les pentes influencent l’usage de la cour, de l’accès, de la plateforme ou de l’aménagement extérieur.",
    },
    {
        icon: <Droplets className="w-5 h-5" />,
        title: "Écoulement des eaux",
        text: "Les surfaces extérieures doivent être pensées pour limiter les stagnations d’eau et favoriser un usage durable de l’espace.",
    },
    {
        icon: <Shield className="w-5 h-5" />,
        title: "Stabilité du terrain",
        text: "Les talus, pentes et différences de niveaux doivent être étudiés afin de choisir une solution adaptée au terrain.",
    },
];

const POURQUOI = [
    {
        title: "Analyse du terrain",
        text: "Chaque demande est étudiée selon les accès, les niveaux, la pente, l’usage prévu, les matériaux et les contraintes du site.",
    },
    {
        title: "Préparation soignée",
        text: "Nous accordons une attention particulière à la préparation du terrain, aux pentes, aux surfaces et aux contraintes d’accès.",
    },
    {
        title: "Solutions adaptées",
        text: "Les aménagements sont pensés selon la configuration du terrain, les usages souhaités et les contraintes techniques du chantier.",
    },
    {
        title: "Enrochement adapté",
        text: "L’enrochement est étudié selon la pente, la stabilité du terrain, la fonction recherchée et l’intégration dans l’espace extérieur.",
    },
    {
        title: "Intervention locale",
        text: "Basés à Plan-les-Ouates, nous intervenons à Genève, dans le canton de Genève et dans toute la Suisse romande.",
    },
    {
        title: "Suivi du projet",
        text: "Nous privilégions des échanges clairs afin de faciliter l’organisation du chantier et la réalisation des prestations prévues.",
    },
];

const FAQS = [
    {
        question: "Quelle est la différence entre enrochement décoratif et enrochement de soutènement ?",
        answer: "Un enrochement décoratif sert principalement à structurer ou valoriser un espace extérieur avec des blocs naturels. Un enrochement de soutènement a une fonction plus technique : il peut accompagner un talus, gérer une différence de niveau ou contribuer à stabiliser une zone. Le choix dépend de la pente, du terrain, de l’usage prévu et des contraintes du site.",
    },
    {
        question: "Faut-il une autorisation pour construire un mur de soutènement ou un enrochement ?",
        answer: "Cela dépend de la commune, de la hauteur, de la localisation, de la pente, de l’ampleur des travaux et de la situation du terrain. Certains aménagements ou soutènements peuvent nécessiter une autorisation ou une vérification préalable. La situation doit être analysée au cas par cas.",
    },
    {
        question: "D’où proviennent les blocs d’enrochement ?",
        answer: "Les blocs utilisés pour un enrochement sont choisis selon le projet, l’aspect souhaité, la fonction recherchée, les contraintes d’accès et les disponibilités. Le choix exact des matériaux est défini selon les besoins du chantier et les possibilités d’approvisionnement.",
    },
    {
        question: "Un enrochement peut-il durer dans le temps ?",
        answer: "Un enrochement bien étudié et adapté au terrain peut constituer une solution robuste pour structurer un extérieur, accompagner un talus ou gérer une différence de niveau. Sa tenue dépend toutefois de la nature du terrain, de l’eau, de la pente, de la mise en œuvre et de l’usage prévu.",
    },
    {
        question: "Réalisez-vous aussi la végétalisation autour de l’enrochement ?",
        answer: "RCPS Terrassement se concentre principalement sur les travaux de terrain, la préparation, la structuration, l’enrochement et les aménagements extérieurs techniques. La végétalisation ou les plantations peuvent être coordonnées selon le projet avec les intervenants appropriés.",
    },
    {
        question: "Combien coûte un aménagement extérieur complet ?",
        answer: "Le coût dépend de la surface, des accès, de la pente, des niveaux, des matériaux, du type d’aménagement, des évacuations nécessaires et de la complexité du chantier. Une analyse du projet permet d’établir une proposition adaptée.",
    },
];

export default function AmenagementsEnrochementPage() {
    const otherServices = SERVICES.filter((s) => s.slug !== SLUG);

    return (
        <>
            <Seo
                title="Aménagements extérieurs Genève | Enrochement et soutènement"
                description="RCPS Terrassement Sàrl réalise vos aménagements extérieurs à Genève et en Suisse romande : cours, accès, allées, plateformes, talus, soutènements, stabilisation de terrain et enrochement."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Prestations", path: "/prestations" },
                    { name: "Aménagements extérieurs & enrochement", path: "/prestations/amenagements-exterieurs-enrochement" },
                ])}
                id="breadcrumb-amenagements"
            />
            <JsonLd
                data={buildService({
                    slug: "amenagements-exterieurs-enrochement",
                    name: "Aménagements extérieurs et enrochement à Genève et en Suisse romande",
                    serviceType: "Aménagements extérieurs, enrochement et soutènement",
                    description:
                        "Travaux d'aménagements extérieurs pour particuliers et professionnels : cours, accès, allées, plateformes, talus, soutènements, stabilisation de terrain et enrochement à Genève et en Suisse romande.",
                    image: `${SITE_ORIGIN}/rcps-assets/prestation-amenagements-01.jpeg`,
                })}
                id="service-amenagements"
            />
            <JsonLd
                data={buildFaqPage(
                    `${SITE_ORIGIN}/prestations/amenagements-exterieurs-enrochement`,
                    FAQS
                )}
                id="faq-amenagements"
            />

            {/* Hero */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="amenagements-hero"
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_elated-beaver-5/artifacts/7n5nyyj1_Ame%CC%81nagements%20exte%CC%81rieurs%20-%20Grande%20-%20Grande.jpeg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-800/80 via-navy-700/70 to-navy-700" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <Reveal>
                            <Breadcrumbs
                                variant="onImage"
                                trail={[
                                    { name: "Accueil", path: "/" },
                                    { name: "Prestations", path: "/prestations" },
                                    { name: "Aménagements extérieurs & enrochement", path: "/prestations/amenagements-exterieurs-enrochement" },
                                ]}
                            />
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="kicker text-white/80 mt-5">
                                <span className="hairline bg-white/60" /> Prestation · Aménagements extérieurs & enrochement
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1
                                className="mt-5 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
                                data-testid="amenagements-h1"
                            >
                                Aménagements extérieurs et enrochement à Genève et en Suisse romande
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-white/85 max-w-2xl text-base md:text-lg leading-relaxed">
                                RCPS Terrassement Sàrl intervient pour les travaux d’aménagements
                                extérieurs, accès, cours, plateformes, talus, soutènements,
                                stabilisation de terrain et enrochement à Genève, dans l’ensemble du
                                canton de Genève et dans toute la Suisse romande.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-4 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
                                Nous accompagnons les particuliers, architectes, régies, promoteurs,
                                entreprises générales, collectivités et professionnels pour préparer,
                                structurer et valoriser leurs espaces extérieurs selon la configuration
                                du terrain, les accès, les niveaux, les usages prévus et les
                                contraintes techniques du chantier.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Reveal delay={0.25}>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-devis"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-navy-700 font-semibold hover:bg-concrete-50 transition"
                            >
                                Demander un devis <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="bg-white" data-testid="amenagements-intro">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-6">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Aménagement extérieur à Genève
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Structurer vos extérieurs avec des solutions adaptées au terrain.
                            </h2>
                        </Reveal>
                        <div className="mt-8 space-y-5 text-navy-700/85 text-base md:text-lg leading-[1.8]">
                            <Reveal delay={0.08}><p>Un aménagement extérieur réussi commence par une bonne préparation du terrain. Avant de créer une cour, une allée, un accès, une plateforme, un talus ou un enrochement, il faut analyser les niveaux, la pente, l’écoulement des eaux, la nature du sol, les accès au chantier et l’usage futur de l’espace.</p></Reveal>
                            <Reveal delay={0.12}><p>À Genève comme en Suisse romande, les espaces extérieurs doivent souvent répondre à plusieurs objectifs : faciliter les accès, stabiliser un terrain, créer une surface fonctionnelle, gérer une différence de niveau, préparer un revêtement ou intégrer un soutènement. RCPS Terrassement étudie chaque projet afin de proposer une intervention adaptée, réaliste et cohérente avec les contraintes du site.</p></Reveal>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <Reveal delay={0.1}>
                            <div className="rounded-md overflow-hidden border border-concrete-200 aspect-[4/3]">
                                <img src="/rcps-assets/prestation-amenagements-01.jpeg" alt="Aménagement extérieur RCPS" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Prestations */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="amenagements-prestations"
            >
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-9">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Nos travaux extérieurs
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Des aménagements pour structurer, stabiliser et valoriser vos extérieurs.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {PRESTATIONS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.05}>
                                <article
                                    className="h-full bg-white rounded-md border border-concrete-200 p-7 md:p-8"
                                    data-testid={`prestation-card-${i + 1}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center flex-shrink-0">
                                            {p.icon}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-mono-num text-xs text-steel">
                                                {String(i + 1).padStart(2, "0")}
                                            </p>
                                            <h3 className="mt-1 font-heading text-xl md:text-2xl font-semibold text-navy-700 tracking-tight">
                                                {p.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="mt-5 text-navy-700/80 leading-relaxed">{p.text}</p>
                                    <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                                        {p.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-navy-700 text-sm"
                                            >
                                                <span className="mt-1.5 w-3.5 h-3.5 rounded-full bg-concrete-100 flex items-center justify-center flex-shrink-0">
                                                    <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                                </span>
                                                <span className="leading-relaxed">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Types de projets */}
            <section className="bg-white" data-testid="amenagements-project-types">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Pour quels projets ?
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Aménagements extérieurs pour particuliers, professionnels et collectivités.
                            </h2>
                            <p className="mt-6 text-navy-700/80 leading-relaxed">
                                RCPS Terrassement intervient sur des projets extérieurs de tailles et
                                de configurations variées à Genève et en Suisse romande. Chaque
                                demande est étudiée selon la localisation du chantier, les accès, les
                                niveaux, l’usage prévu, la pente, la nature du terrain, les matériaux
                                et les contraintes logistiques.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="mb-5 aspect-[16/10] rounded-md overflow-hidden border border-concrete-200">
                                <img
                                    src="/rcps-assets/prestation-amenagements-02.jpeg"
                                    alt="Aménagement extérieur et enrochement RCPS"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <ul className="grid sm:grid-cols-2 gap-2.5">
                                {PROJECT_TYPES.map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3 bg-concrete-50 border border-concrete-200 rounded-md px-4 py-3 text-navy-700 text-sm md:text-base leading-snug"
                                    >
                                        <span className="mt-1 w-4 h-4 rounded-full bg-white border border-concrete-200 flex items-center justify-center flex-shrink-0">
                                            <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                        </span>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Méthode */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="amenagements-methode"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Notre méthode
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight">
                                    Une intervention préparée selon le terrain et l’usage prévu.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {METHODE.map((m, i) => (
                            <Reveal key={m.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`methode-step-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        Étape {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg md:text-xl font-semibold">
                                        {m.title}
                                    </h3>
                                    <p className="mt-3 text-white/75 text-sm leading-relaxed">
                                        {m.text}
                                    </p>
                                    <ul className="mt-4 space-y-1.5">
                                        {m.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-white/75 text-sm"
                                            >
                                                <span className="mt-1.5 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
                                                <span className="leading-snug">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Points de vigilance */}
            <InlineCTA
                variant="navy"
                title="Un projet d’aménagement extérieur ou d’enrochement ?"
                text="Pentes, écoulements, accès, intégration au site : parlez-nous de votre projet, nous étudions votre demande et revenons vers vous."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "À propos de nous", to: "/entreprise" }}
                dataTestId="amenagements-inline-cta"
            />
            <section className="bg-white" data-testid="amenagements-vigilance">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-12">
                        <div className="md:col-span-5">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> À prendre en compte
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-[1.15]">
                                    Aménagement extérieur : niveaux, eaux, accès et stabilité du terrain.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7 space-y-5 text-navy-700/80 leading-[1.8]">
                            <Reveal delay={0.08}>
                                <p>
                                    Un aménagement extérieur doit être pensé selon plusieurs
                                    paramètres techniques : accès au chantier, pente, écoulement des
                                    eaux, stabilité du terrain, portance, matériaux, voisinage,
                                    usages futurs et éventuelles contraintes communales.
                                </p>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <p>
                                    À Genève et en Suisse romande, certains travaux peuvent
                                    nécessiter des informations techniques complémentaires ou des
                                    démarches spécifiques selon la commune, le site, la pente, la
                                    nature des travaux et l’ampleur de l’aménagement. RCPS
                                    Terrassement analyse ces éléments pour proposer une intervention
                                    adaptée.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.16}>
                        <div className="mb-10 md:mb-12 aspect-[21/9] rounded-md overflow-hidden border border-concrete-200">
                            <img
                                src="/rcps-assets/prestation-amenagements-03.jpeg"
                                alt="Aménagement extérieur RCPS — pentes, écoulement et intégration au site"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5">
                        {VIGILANCE.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-concrete-200 bg-concrete-50 p-6 md:p-7"
                                    data-testid={`vigilance-card-${i + 1}`}
                                >
                                    <div className="w-11 h-11 rounded-md bg-white text-navy-700 border border-concrete-200 flex items-center justify-center">
                                        {v.icon}
                                    </div>
                                    <p className="mt-5 font-mono-num text-xs text-steel">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-1 font-heading text-lg md:text-xl font-semibold text-navy-700 tracking-tight">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-navy-700/75 text-sm md:text-base leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pourquoi RCPS */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="amenagements-pourquoi"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Pourquoi RCPS ?
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight">
                                    Une approche structurée pour vos extérieurs et enrochements.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="mb-10 aspect-[21/9] rounded-md overflow-hidden border border-white/12">
                            <img
                                src="/rcps-assets/prestation-amenagements-04.jpeg"
                                alt="Aménagements extérieurs et enrochement RCPS — chantier structuré et adapté au terrain"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {POURQUOI.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`pourquoi-card-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg font-semibold">
                                        {h.title}
                                    </h3>
                                    <p className="mt-2 text-white/75 text-sm leading-relaxed">
                                        {h.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ancrage local (avec image) */}
            <section className="bg-white" data-testid="amenagements-ancrage">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-7 order-2 md:order-1">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Ancrage local
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Basés à Genève, actifs sur vos extérieurs en Suisse romande.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="mt-6 text-navy-700/80 text-base md:text-lg leading-[1.8]">
                                RCPS Terrassement Sàrl accompagne les particuliers, architectes,
                                régies, promoteurs, entreprises générales, collectivités et
                                professionnels dans leurs travaux d’aménagements extérieurs et
                                d’enrochement. Basés à Plan-les-Ouates, nous intervenons à Genève,
                                dans l’ensemble du canton et dans toute la Suisse romande.
                            </p>
                            <p className="mt-4 text-navy-700/80 text-base md:text-lg leading-[1.8]">
                                Notre approche repose sur l’analyse du terrain, la préparation des
                                accès, la gestion des niveaux, la stabilisation des zones sensibles
                                et la réalisation de solutions extérieures adaptées à l’usage prévu.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-5 order-1 md:order-2">
                        <Reveal delay={0.05}>
                            <div className="relative rounded-md overflow-hidden border border-concrete-200 aspect-[4/5] group">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_elated-beaver-5/artifacts/7n5nyyj1_Ame%CC%81nagements%20exte%CC%81rieurs%20-%20Grande%20-%20Grande.jpeg"
                                    alt="Aménagement extérieur — enrochement et talus"
                                    className="w-full h-full object-cover transition duration-[900ms] group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-transparent" />
                                <div className="absolute bottom-5 left-5 right-5 text-white">
                                    <p className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-white/80 flex items-center gap-2">
                                        <MapPinned className="w-3 h-3" /> Genève · Suisse romande
                                    </p>
                                    <p className="mt-2 font-heading text-lg md:text-xl font-semibold leading-tight">
                                        Travaux extérieurs adaptés au terrain.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Contact / Formulaire */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="amenagements-contact"
            >
                <div className="rcps-container py-20 md:py-24 grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Contact
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 leading-tight tracking-tight">
                                Parlons de votre projet d’aménagement extérieur ou d’enrochement.
                            </h2>
                            <p className="mt-5 text-navy-700/80 leading-relaxed">
                                Vous souhaitez créer une cour, aménager un accès, préparer une
                                plateforme, stabiliser un talus, réaliser un enrochement ou
                                structurer vos extérieurs ? Transmettez-nous les informations
                                principales de votre chantier. RCPS Terrassement vous répondra après
                                analyse de votre demande.
                            </p>
                            <ul className="mt-6 space-y-2 text-navy-700/80 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Analyse selon le terrain, les accès et les niveaux</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Prise en compte de l’usage prévu et des contraintes</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Réponse après étude de votre demande</span>
                                </li>
                            </ul>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.08}>
                            <ContactForm defaultProjectType="Aménagements extérieurs & enrochement" />
                            <p className="mt-4 text-xs md:text-sm text-navy-700/60 leading-relaxed">
                                Réponse après analyse de votre demande · devis personnalisé · sans
                                engagement.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                className="bg-white border-t border-concrete-200"
                data-testid="amenagements-faq"
            >
                <div className="rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                        <div className="md:col-span-5 md:sticky md:top-28">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Questions fréquentes
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-tight">
                                    Vos questions sur les aménagements extérieurs et l’enrochement.
                                </h2>
                                <p className="mt-5 text-navy-700/80 leading-relaxed">
                                    Vous avez une question sur un aménagement extérieur, un accès,
                                    une cour, un talus ou un enrochement à Genève ou en Suisse
                                    romande ? Contactez-nous pour présenter votre terrain, sa
                                    localisation et les travaux souhaités.
                                </p>
                                <div className="mt-6 flex items-center gap-3 text-navy-700">
                                    <span className="w-10 h-10 rounded-md bg-concrete-100 flex items-center justify-center">
                                        <HelpCircle className="w-5 h-5" />
                                    </span>
                                    <Link
                                        to="/contact"
                                        data-testid="faq-contact-link"
                                        className="text-sm font-semibold link-underline"
                                    >
                                        Poser une question à RCPS
                                    </Link>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7">
                            <Reveal delay={0.08}>
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="w-full space-y-3"
                                    data-testid="amenagements-faq-accordion"
                                >
                                    {FAQS.map((f, i) => (
                                        <AccordionItem
                                            key={f.question}
                                            value={`item-${i}`}
                                            className="border border-concrete-200 rounded-md bg-white overflow-hidden [&[data-state=open]]:border-navy-700 transition"
                                        >
                                            <AccordionTrigger
                                                className="px-5 py-4 md:px-6 md:py-5 text-left hover:no-underline group"
                                                data-testid={`faq-question-${i}`}
                                            >
                                                <div className="flex items-start gap-4 pr-2">
                                                    <span className="font-mono-num text-xs text-navy-700/50 mt-1 flex-shrink-0">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="font-heading text-navy-700 font-semibold text-base md:text-lg leading-snug">
                                                        {f.question}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-5 md:px-6 pb-5 md:pb-6">
                                                <div className="pl-9 text-navy-700/80 text-sm md:text-base leading-[1.75]">
                                                    {f.answer}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <PrestationInternalLinks
                currentSlug={SLUG}
                relatedCategories={[
                    "Aménagements extérieurs & enrochement",
                    "Terrassement",
                    "Génie civil, voirie & enrobés",
                ]}
                bridgeText={
                    <>
                        <p>
                            Les aménagements extérieurs et l’enrochement s’inscrivent souvent dans une
                            démarche plus large : préparation du terrain, gestion des niveaux, création
                            d’accès et intégration paysagère du bâti.
                        </p>
                        <p>
                            Avant tout aménagement, nous réalisons les{" "}
                            <Link
                                to="/prestations/terrassement"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                travaux de terrassement à Genève et en Suisse romande
                            </Link>
                            {" "}: décaissement, nivellement, gestion des volumes et évacuation des
                            terres selon la configuration du site.
                        </p>
                        <p>
                            Pour la création d’un accès carrossable, d’un chemin ou d’une surface
                            structurée après l’aménagement, découvrez nos prestations de{" "}
                            <Link
                                to="/prestations/genie-civil-voirie-enrobes"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                génie civil, voirie et enrobés à Genève
                            </Link>
                            . Et sur un site à réhabiliter, nous accompagnons également la phase de{" "}
                            <Link
                                to="/prestations/demolition-deconstruction"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                démolition et déconstruction à Genève et en Suisse romande
                            </Link>
                            .
                        </p>
                    </>
                }
            />

            {/* CTA final */}
            <SectionCTA
                title="Un projet d’aménagement extérieur ou d’enrochement à concrétiser ?"
                text="Présentez-nous votre chantier. Nous étudions votre demande à Genève ou en Suisse romande selon la localisation, les accès, les niveaux, la pente, l’usage prévu, les matériaux et les contraintes techniques."
            />
        </>
    );
}
